import { AnalyticsContext, ContextType } from '@carvana/eventable-clickstream';
import { ReactNode, useContext } from 'react';
import DsModal from '@/common/DsModal';
import MediaContent from '@/common/helios/Modals/GeoModal/MediaContent';
import ModalContents from '@/common/helios/Modals/GeoModal/ModalContents';
import { useIsDesktop, useIsMobile } from '~/context/common/DeviceContext';
import LocationContextProvider, {
  LocationContext
} from '~/context/common/LocationContext';
import { useUserLocation } from '~/context/common/UserContext';
import type { DeliveryApi } from '~/contracts/common/Geolocation';
import useGeolocationConfig from '~/hooks/common/geolocation/useGeolocationConfig';

type OwnProps = {
  AvailabilityOptions?: ReactNode;
  fetchDelivery?: DeliveryApi;
  hidePickup?: boolean;
  isGeolocationModalOpen: boolean;
  onSuccessfulZipChange?: () => void;
  setGeolocationModalOpen: (open: boolean) => void;
  trackEvent?: ContextType['trackEvent'];
};

function GeoModal({
  AvailabilityOptions,
  hidePickup,
  isGeolocationModalOpen,
  setGeolocationModalOpen
}: OwnProps) {
  const { isDeliveryAllowed = false, pickUpInfoToDisplay = null } =
    useContext(LocationContext);
  const isDesktop = useIsDesktop();
  const isMobile = useIsMobile();

  const willHaveVideoContent =
    !isMobile &&
    (isDeliveryAllowed ||
      pickUpInfoToDisplay?.locationType === 'VendingMachine');

  const desktopModalWidth = willHaveVideoContent ? '1008px' : '504px';

  return (
    <DsModal
      mediaComponent={willHaveVideoContent ? <MediaContent /> : undefined}
      mobileStyle="inset"
      onClose={() => {
        setGeolocationModalOpen(false);
      }}
      isOpen={isGeolocationModalOpen}
      desktopWidth={isDesktop ? desktopModalWidth : '90vw'}
      classes={{
        body: isMobile ? 'py-2xl' : '',
        container: 'max-h-[95%]'
      }}
    >
      <ModalContents
        AvailabilityOptions={AvailabilityOptions}
        hidePickup={hidePickup}
      />
    </DsModal>
  );
}

export default function GeolocationModalWrapper(props: OwnProps) {
  const {
    fetchDelivery,
    isGeolocationModalOpen,
    onSuccessfulZipChange,
    setGeolocationModalOpen,
    trackEvent: customTrackEvent
  } = props;
  const { trackEvent: analyticsTrackEvent } = useContext(AnalyticsContext);
  const { zip } = useUserLocation();
  const config = useGeolocationConfig({
    onSuccessfulZipChange,
    setGeolocationModalOpen
  });

  return (
    <LocationContextProvider
      currentZip={zip}
      isOpen={isGeolocationModalOpen}
      trackEvent={customTrackEvent ?? analyticsTrackEvent}
      {...config}
      fetchDelivery={fetchDelivery ?? config.fetchDelivery}
    >
      <GeoModal {...props} />
    </LocationContextProvider>
  );
}
